import * as React from 'react';
import './AppliedTextFilterChip.scss';

export interface AppliedFilterChipDefinition {
  filterKey: string;
  label: string;
  type?: 'text' | 'date' | 'multi';
  valueLabels?: { [value: string]: string };
}

interface Props {
  history: any;
  location: any;
  filterKey?: string;
  label?: string;
  filters?: AppliedFilterChipDefinition[];
}

const formatDate = (value: string): string => {
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return value;
  return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC'
  });
};

const formatValues = (definition: AppliedFilterChipDefinition, rawValues: string[]): string => {
  const values = rawValues.filter(value => value && value !== 'undefined' && value !== 'null');

  if (definition.type === 'date') {
    let from = '';
    let to = '';
    values.forEach(value => {
      const [part, ...rest] = value.split('_');
      const date = rest.join('_');
      if (!date || date === 'undefined' || date === 'null') return;
      if (part === 'from') from = date;
      else if (part === 'to') to = date;
      else if (!from) from = value;
    });
    if (from && to) return `${formatDate(from)} – ${formatDate(to)}`;
    if (from) return `from ${formatDate(from)}`;
    if (to) return `to ${formatDate(to)}`;
    return '';
  }

  const labelledValues = values.map(value => definition.valueLabels?.[value] || value);
  if (definition.type === 'multi' && labelledValues.length > 3) return `${labelledValues.length} selected`;
  return labelledValues.join(', ');
};

export const AppliedTextFilterChip: React.FC<Props> = ({ history, location, filterKey, label, filters }) => {
  const params = new URLSearchParams((location && location.search) || '');
  const definitions: AppliedFilterChipDefinition[] =
    filters && filters.length
      ? filters
      : filterKey && label
      ? [{ filterKey, label, type: 'text' }]
      : [];

  const applied = definitions
    .map(definition => ({
      definition,
      value: formatValues(definition, params.getAll(definition.filterKey))
    }))
    .filter(item => item.value);

  if (!applied.length) return null;

  const clear = (key: string) => {
    params.delete(key);
    params.delete('page');
    history.push({
      pathname: location.pathname,
      search: params.toString()
    });
  };

  return (
    <div className="parentAppliedFilters" aria-label="Applied filters">
      {applied.map(({ definition, value }) => (
        <span className="parentAppliedFilterChip" key={definition.filterKey}>
          <span>
            {definition.label}: {value}
          </span>
          <button
            type="button"
            onClick={() => clear(definition.filterKey)}
            aria-label={`Remove ${definition.label.toLowerCase()} filter`}
          >
            ×
          </button>
        </span>
      ))}
    </div>
  );
};
